import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const METHOD_HTTP = {
  view: "GET",
  create: "POST",
  update: "PUT",
  delete: "DELETE"
};

const ACTION_LABELS = {
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete"
};

const MyApiCard = ({ api, deleting, onView, onEdit, onDelete }) => {
  const { t } = useTranslation("common");

  const methodBadge = useMemo(() => {
    const http = METHOD_HTTP[api.action] || "GET";
    const label = t(ACTION_LABELS[api.action] || "View");
    return `${http} · ${label}`;
  }, [api.action, t]);

  const fields = api.fields || api.inputFields;
  const fieldsDisplay =
    fields && fields.length > 0 ? fields.join(", ") : t("— not set —");

  return (
    <div className="api-card">
      <div className="api-card-header">
        <div className="api-card-name">{api.filename}</div>
        <span className={`api-method-badge ${api.action || "view"}`}>{methodBadge}</span>
      </div>
      <div className="api-card-body">
        <div className="api-field">
          <span className="lbl">{t("Config:")}</span>
          <span className="val">{api.config}</span>
        </div>
        <div className="api-field">
          <span className="lbl">{t("Model:")}</span>
          <span className="val model">{api.model}</span>
        </div>
        <div className="api-field">
          <span className="lbl">{t("Action:")}</span>
          <span className="val">{api.action}</span>
        </div>
        {api.record ? (
          <div className="api-field">
            <span className="lbl">{t("Record ID:")}</span>
            <span className="val">{api.record}</span>
          </div>
        ) : (
          <div className="api-field">
            <span className="lbl">{t("Fields:")}</span>
            <span className={`val ${!fields || fields.length === 0 ? "muted" : ""}`}>
              {fieldsDisplay}
            </span>
          </div>
        )}
      </div>
      <div className="api-card-footer">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onView(api)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>{t("View")}</span>
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(api)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          <span>{t("Edit")}</span>
        </button>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={deleting}
          onClick={() => onDelete(api)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14H6L5 6" />
            <path d="M9 6V4h6v2" />
          </svg>
          <span>{t("Delete")}</span>
        </button>
      </div>
    </div>
  );
};

MyApiCard.propTypes = {
  api: PropTypes.shape({
    filename: PropTypes.string,
    config: PropTypes.string,
    model: PropTypes.string,
    action: PropTypes.string,
    fields: PropTypes.array,
    inputFields: PropTypes.array,
    record: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    filePath: PropTypes.string
  }).isRequired,
  deleting: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

MyApiCard.defaultProps = {
  deleting: false
};

export default MyApiCard;
